import { createAction, createReducer } from '@reduxjs/toolkit';
import type { RootState } from '@store/types';
import type { AppDispatch } from '@reducers/utils/types';
import type {
  RetrieveQueueState,
  GenerateKeyParams,
  EnqueueRequestPayload,
  ResolveQueuePayload,
  RejectQueuePayload,
  RequestFunction,
} from './types';

// Re-export types for consumers
export type {
  RetrieveQueueState,
  GenerateKeyParams,
  EnqueueRequestPayload,
  ResolveQueuePayload,
  RejectQueuePayload,
  RequestFunction,
} from './types';

// === State ===

const initState: RetrieveQueueState = {
  activeRequests: {},
  pendingQueues: {},
};

// === Actions ===

export const deleteKeyFromActiveRequestAction = createAction<string>('retrieveQueue/deleteKeyFromActiveRequestAction');
export const addActiveRequestAction = createAction<string>('retrieveQueue/addActiveRequestAction');
export const finishActiveRequestAction = createAction<string>('retrieveQueue/finishActiveRequestAction');
export const enqueueRequestAction = createAction<EnqueueRequestPayload>('retrieveQueue/enqueueRequestAction');
export const resolveQueueAction = createAction<ResolveQueuePayload>('retrieveQueue/resolveQueueAction');
export const rejectQueueAction = createAction<RejectQueuePayload>('retrieveQueue/rejectQueueAction');
export const clearQueuesAction = createAction('retrieveQueue/clearQueuesAction');

// === Helpers ===

const generateKey = (params: GenerateKeyParams): string => {
  const { documentKey, ids, retrieveAllDocuments, storeMode, impactMainStore } = params || {};
  // Add context isolation to prevent queue key collisions between main store and operator panel
  const contextPrefix = impactMainStore ? 'MAIN' : 'OPERATOR';
  const key = `${contextPrefix}-${documentKey}-${JSON.stringify(ids?.sort())}-${retrieveAllDocuments}-${storeMode}-${impactMainStore}`;
  return key;
};

// === Thunks ===

export const enqueue =
  (requestFunction: RequestFunction, params: GenerateKeyParams) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<unknown> => {
    const key = generateKey(params);

    const { activeRequests, pendingQueues } = getState().retrieveQueue;

    return new Promise((resolve, reject) => {
      if (activeRequests[key]) {
        if (!pendingQueues[key]) {
          dispatch(enqueueRequestAction({ key, request: { resolve, reject, params } }));
        } else {
          dispatch(enqueueRequestAction({ key, request: { resolve, reject, params } }));
        }
        return;
      }

      dispatch(executeRequest(key, requestFunction, params, resolve, reject));
    });
  };

export const executeRequest =
  (
    key: string,
    requestFunction: RequestFunction,
    params: GenerateKeyParams,
    resolve: (value: unknown) => void,
    reject: (reason?: unknown) => void,
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    try {
      dispatch(addActiveRequestAction(key));

      const result = await dispatch(requestFunction(params));

      resolve(result);

      await dispatch(processPendingQueue(key, result));
    } catch (error) {
      reject(error);
      dispatch(processPendingQueueError(key, error));
    } finally {
      dispatch(finishActiveRequestAction(key));
    }
  };

export const processPendingQueue =
  (key: string, result: unknown) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const { pendingQueues } = getState().retrieveQueue;
    const pendingRequests = pendingQueues[key];

    if (pendingRequests && pendingRequests.length > 0) {
      pendingRequests.forEach(({ resolve }) => resolve(result));
      dispatch(resolveQueueAction({ key, result }));
    }
  };

export const processPendingQueueError =
  (key: string, error: unknown) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { pendingQueues } = getState().retrieveQueue;
    const pendingRequests = pendingQueues[key];

    if (pendingRequests && pendingRequests.length > 0) {
      pendingRequests.forEach(({ reject }) => reject(error));
      dispatch(rejectQueueAction({ key, error }));
    }
  };

export const clear = () => (dispatch: AppDispatch): void => {
  dispatch(clearQueuesAction());
};

export const getStatus =
  () =>
  (dispatch: AppDispatch, getState: () => RootState): { activeRequests: string[]; pendingQueues: Record<string, number> } => {
    const { activeRequests, pendingQueues } = getState().retrieveQueue;
    return {
      activeRequests: Object.keys(activeRequests),
      pendingQueues: Object.fromEntries(Object.entries(pendingQueues).map(([key, queue]) => [key, queue.length])),
    };
  };

// === Reducer ===

export default createReducer<RetrieveQueueState>(initState, {
  [addActiveRequestAction.type]: (state, action) => {
    state.activeRequests[action.payload] = true;
  },

  [finishActiveRequestAction.type]: (state, action) => {
    delete state.activeRequests[action.payload];
  },

  [deleteKeyFromActiveRequestAction.type]: (state, action) => {
    delete state.activeRequests[action.payload];
  },

  [enqueueRequestAction.type]: (state, action) => {
    const { key, request } = action.payload;
    if (!state.pendingQueues[key]) {
      state.pendingQueues[key] = [];
    }
    state.pendingQueues[key].push(request);
  },

  [resolveQueueAction.type]: (state, action) => {
    const { key } = action.payload;
    delete state.pendingQueues[key];
  },

  [rejectQueueAction.type]: (state, action) => {
    const { key } = action.payload;
    delete state.pendingQueues[key];
  },

  [clearQueuesAction.type]: (state) => {
    state.activeRequests = {};
    state.pendingQueues = {};
  },
});
